import { Head, Link, router } from '@inertiajs/react'
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { usePage } from '@inertiajs/react'

import { editFundPath, fundPath, fundsPath } from '@/lib/routes'
import type { SharedPageProps } from '@/types/inertia'

type FundShow = {
  id: number
  slug: string
  name: string
  vintage_year: number
  investment_count: number
  commitment_total: string
  invested_total: string
  has_dependents: boolean
}

type FundsShowProps = {
  fund: FundShow
}

function formatMoney(value: string): string {
  const num = Number(value)
  if (Number.isNaN(num)) return value
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

function FundsShow({ fund }: FundsShowProps) {
  const { auth } = usePage<SharedPageProps>().props
  const isPartner = auth.user?.role === 'partner'

  return (
    <>
      <Head title={`${fund.name} — Funds`} />

      <div className="mx-auto max-w-5xl px-8 py-10">
        <Link
          href={fundsPath()}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to funds
        </Link>

        <PageHeader
          title={fund.name}
          description={`Vintage ${fund.vintage_year} · ${fund.slug}`}
          actions={
            isPartner ? (
              <div className="flex gap-2">
                <Link href={editFundPath(fund.slug)}>
                  <Button variant="outline">
                    <Pencil />
                    Edit
                  </Button>
                </Link>
                {!fund.has_dependents && (
                  <Button
                    variant="destructive"
                    onClick={() => {
                      if (window.confirm(`Archive ${fund.name}?`)) {
                        router.delete(fundPath(fund.slug))
                      }
                    }}
                  >
                    <Trash2 />
                    Delete
                  </Button>
                )}
              </div>
            ) : null
          }
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Committed capital
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold tabular-nums">
              {formatMoney(fund.commitment_total)}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Invested
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold tabular-nums">
              {formatMoney(fund.invested_total)}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Portfolio investments
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold tabular-nums">
              {fund.investment_count}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

export default Object.assign(FundsShow, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
